// Facturation > « À encaisser » (Q-clients, 02/11/2026) : miroir de J1 côté
// ventes. Client et compte client, n° de facture, date, échéance, reste dû ;
// onglets À encaisser / Encaissées, à rapprocher / Toutes ; filtre par client,
// tri par date, total à encaisser général et par client ; marqueur « Déjà
// encaissée — à rapprocher » (aucune écriture, levé au rapprochement).
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import InvoicePdfButton from "./InvoicePdfButton";
import DocBadge, { ManualPaymentBadge } from "./DocBadge";
import { useAuth } from "../contexts/AuthContext";

const SETTLED_MARKED = "encaissée, à rapprocher";
const isToCollect = (i) => i.payment_state === "à encaisser" || i.payment_state === "partiellement encaissée";
const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

export default function SalesReceivables({ onNavigate }) {
  const { user } = useAuth();
  const canMark = ["admin", "comptable"].includes(user?.role);
  const [view, setView] = useState("to_collect");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [dateSort, setDateSort] = useState("desc");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    setError("");
    api.invoices.receivables(view).then(d => { setData(d); setSelected([]); }).catch(e => setError(e.message));
  }, [view]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setClientFilter(""); setResult(null); }, [view]);

  async function mark(ids, marked) {
    if (!ids.length) return;
    if (marked && !window.confirm(`Marquer ${ids.length} facture(s) « Déjà encaissée — à rapprocher » ?\n\nAucune écriture n'est passée : elles sortent du « À encaisser » mais restent dues par le client en comptabilité, et la TVA sur les encaissements n'est pas comptée, tant que leur encaissement n'est pas relié au relevé. Le marqueur se lève de lui-même au rapprochement.`)) return;
    setBusy(true); setResult(null); setError("");
    try { setResult(await api.invoices.settledMark(ids, marked)); load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  const openImport = (importId) => {
    try { if (importId) sessionStorage.setItem("execpro360.openImport", String(importId)); } catch { /* facultatif */ }
    onNavigate?.("banque");
  };

  if (error && !data) return <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, fontSize: 13 }}>{error}</div>;
  if (!data) return <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>Chargement…</div>;
  const rows = data.invoices
    .filter(i => !clientFilter || (i.client_name || "—") === clientFilter)
    .sort((a, b) => (dateSort === "asc" ? 1 : -1) * (String(a.date || "").localeCompare(String(b.date || "")) || a.id - b.id));
  const selectable = (i) => isToCollect(i) || i.payment_state === SETTLED_MARKED;
  const selToMark = selected.filter(id => isToCollect(data.invoices.find(i => i.id === id) || {}));
  const selToUnmark = selected.filter(id => (data.invoices.find(i => i.id === id) || {}).payment_state === SETTLED_MARKED);
  const th = { padding: "8px 10px", textAlign: "left", fontWeight: 600 };
  const td = { padding: "8px 10px" };
  const tabBtn = (key, label) => (
    <button key={key} onClick={() => setView(key)} style={{ padding: "5px 12px", borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: "pointer",
      border: "1px solid #e5e7eb", background: view === key ? "#1e40af" : "#fff", color: view === key ? "#fff" : "#4b5563" }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        {tabBtn("to_collect", `À encaisser (${data.to_collect.count} · ${fmt(data.to_collect.remaining)} €)`)}
        {tabBtn("settled_marked", `Encaissées, à rapprocher (${data.settled_marked.count} · ${fmt(data.settled_marked.remaining)} €)`)}
        {tabBtn("all", "Toutes")}
      </div>
      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 8, fontSize: 13 }}>{error}</div>}
      {view === "to_collect" && (
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <div>Total à encaisser : <strong>{fmt(data.to_collect.remaining)} €</strong> sur {data.to_collect.count} facture(s), reste dû calculé d'après les encaissements rapprochés ou ventilés du relevé.</div>
          {data.settled_marked.count > 0 && (
            <div style={{ color: "#6d28d9", marginTop: 3 }}>Encaissées, à rapprocher : <strong>{fmt(data.settled_marked.remaining)} €</strong> ({data.settled_marked.count} facture(s) — encore dues en comptabilité tant que l'encaissement n'est pas relié au relevé).</div>
          )}
          {data.to_collect_by_client.length > 0 && (
            <details style={{ marginTop: 4 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5 }}>Total à encaisser par client ({data.to_collect_by_client.length})</summary>
              <table style={{ fontSize: 12, marginTop: 4, borderCollapse: "collapse" }}><tbody>
                {data.to_collect_by_client.map(c => (
                  <tr key={c.client_name}>
                    <td style={{ padding: "2px 12px 2px 0" }}><a href="#c" onClick={e => { e.preventDefault(); setClientFilter(c.client_name); }}>{c.client_account ? `${c.client_account} — ` : ""}{c.client_name}</a></td>
                    <td style={{ padding: "2px 12px 2px 0", color: "#6b7280" }}>{c.count} facture(s)</td>
                    <td style={{ padding: "2px 0", textAlign: "right", fontWeight: 600 }}>{fmt(c.remaining)} €</td>
                  </tr>
                ))}
              </tbody></table>
            </details>
          )}
        </div>
      )}
      {view === "settled_marked" && data.invoices.length > 0 && (
        <div style={{ fontSize: 13, marginBottom: 8, color: "#6d28d9" }}>Encaissées, à rapprocher : <strong>{fmt(data.remaining)} €</strong> — encore dues en comptabilité tant que l'encaissement n'est pas relié au relevé. Reliez-les dans Comptes bancaires > « 🔎 Proposer les factures », onglet « Encaissements » : le marqueur se lève alors de lui-même.</div>
      )}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 12.5, marginBottom: 8 }}>
        <label>Client :{" "}
          <select className="input" style={{ fontSize: 12, padding: "3px 8px", width: "auto" }} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
            <option value="">Tous</option>
            {[...new Set(data.invoices.map(i => i.client_name || "—"))].sort((a, b) => a.localeCompare(b, "fr")).map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label>Tri :{" "}
          <select className="input" style={{ fontSize: 12, padding: "3px 8px", width: "auto" }} value={dateSort} onChange={e => setDateSort(e.target.value)}>
            <option value="desc">Date, plus récentes d'abord</option>
            <option value="asc">Date, plus anciennes d'abord</option>
          </select>
        </label>
        {canMark && selected.length > 0 && (
          <>
            <button className="btn primary" style={{ fontSize: 12, padding: "4px 10px" }} disabled={busy || !selToMark.length} onClick={() => mark(selToMark, true)}>Marquer « Déjà encaissée — à rapprocher » ({selToMark.length})</button>
            <button className="btn" style={{ fontSize: 12, padding: "4px 10px" }} disabled={busy || !selToUnmark.length} onClick={() => mark(selToUnmark, false)}>Retirer le marqueur ({selToUnmark.length})</button>
          </>
        )}
      </div>
      {result && (
        <div style={{ fontSize: 12.5, marginBottom: 8, color: result.refused?.length ? "#b45309" : "#166534" }}>
          {result.marked ? `${result.done} facture(s) marquée(s) « Déjà encaissée — à rapprocher ».` : `Marqueur retiré sur ${result.done} facture(s).`}
          {(result.refused || []).map(r => <div key={r.id}>{r.number || `#${r.id}`} : {r.reason}</div>)}
        </div>
      )}
      {rows.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#6b7280", fontSize: 13 }}>{view === "to_collect" ? "Aucune facture à encaisser." : view === "settled_marked" ? "Aucune facture encaissée à rapprocher." : "Aucune facture de vente."}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead><tr style={{ background: "#f8fafc", fontSize: 11, color: "#6b7280", textTransform: "uppercase" }}>
              {canMark && <th style={{ ...th, width: 24 }}><input type="checkbox" title="Tout sélectionner" checked={rows.length > 0 && rows.filter(selectable).every(i => selected.includes(i.id))}
                onChange={e => setSelected(e.target.checked ? rows.filter(selectable).map(i => i.id) : [])} /></th>}
              <th style={th}>Client</th><th style={th}>N° facture</th><th style={th}>Date</th><th style={th}>Échéance</th>
              <th style={{ ...th, textAlign: "right" }}>TTC</th><th style={th}>Encaissement</th><th style={th}>Actions</th>
            </tr></thead>
            <tbody>{rows.map(i => (
              <tr key={i.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                {canMark && <td style={td}>{selectable(i) && <input type="checkbox" checked={selected.includes(i.id)} onChange={e => setSelected(s => e.target.checked ? [...s, i.id] : s.filter(x => x !== i.id))} />}</td>}
                <td style={{ ...td, fontWeight: 600 }}>{i.client_name || "—"}{i.client_account && <div style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 400, color: "#6b7280" }}>{i.client_account}</div>}</td>
                <td style={{ ...td, fontFamily: "monospace" }}><DocBadge kind="vente" />{i.number} <InvoicePdfButton kind="vente" id={i.id} />{(i.payments || []).some(x => x.origin === "saisie") && <ManualPaymentBadge />}</td>
                <td style={td}>{fmtDate(i.date)}</td>
                <td style={td}>{fmtDate(i.due_date)}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmt(i.ttc_amount)} €</td>
                <td style={{ ...td, fontSize: 11.5 }}>
                  {i.payment_state === SETTLED_MARKED ? (
                    <>
                      <span style={{ fontWeight: 700, color: "#6d28d9", background: "#6d28d914", padding: "2px 7px", borderRadius: 8 }}>Encaissée — à rapprocher</span>
                      <div style={{ color: "#6b7280" }}>marquée{i.settled_marked_by_name ? ` par ${i.settled_marked_by_name}` : ""} le {fmtDate(i.settled_marked_at)}</div>
                      <div>Encore due en comptabilité : <strong>{fmt(i.remaining)} €</strong></div>
                    </>
                  ) : (
                    <span style={{ fontWeight: 700, color: i.payment_state === "encaissée" ? "#16a34a" : i.payment_state === "à encaisser" ? "#b45309" : "#1d4ed8" }}>
                      {i.payment_state === "encaissée" ? "Encaissée" : i.payment_state === "à encaisser" ? "À encaisser" : "Partiellement encaissée"}
                    </span>
                  )}
                  {isToCollect(i) && <div>Reste dû : <strong>{fmt(i.remaining)} €</strong></div>}
                  {(i.payments || []).map((x, k) => (
                    <div key={k} style={{ color: "#6b7280" }}>
                      {fmtDate(x.date)} · {fmt(x.amount)} € {x.origin === "ventilation" ? "(ventilé)" : x.origin === "saisie" ? "(saisi)" : ""}
                      {x.import_id && <> · <a href="#releve" onClick={e => { e.preventDefault(); openImport(x.import_id); }}>ligne du relevé</a></>}
                    </div>
                  ))}
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  {canMark && isToCollect(i) && <button className="btn" style={{ fontSize: 11, padding: "3px 8px" }} disabled={busy} onClick={() => mark([i.id], true)}
                    title="Encaissement déjà reçu, pas encore relié au relevé : sort du « À encaisser », aucune écriture">Déjà encaissée</button>}
                  {canMark && i.payment_state === SETTLED_MARKED && <button className="btn" style={{ fontSize: 11, padding: "3px 8px" }} disabled={busy} onClick={() => mark([i.id], false)}
                    title="Retirer le marqueur : la facture revient dans « À encaisser »">Retirer le marqueur</button>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
